import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { formatNumber } from 'utils/formats';

import { IncentiveOfferSellerParticipationLimits } from 'modules/incentivesMarket/types';
import { calculatePreviewMaxUnitsSellerCanOffer } from 'modules/incentivesMarket/utils/getSellerLimitsSummary';

import { PREVIEW_AVAILABLE_UNITS } from '../../constants/defaultSellerParticipationLimits';

type BuyOfferSellerLimitsPreviewProps = {
  sellerParticipationLimits: IncentiveOfferSellerParticipationLimits;
  sharesToTransfer: number;
  unitPrice: number;
  availablePlanTypes?: string[];
};

const BuyOfferSellerLimitsPreview: FC<BuyOfferSellerLimitsPreviewProps> = ({
  sellerParticipationLimits,
  sharesToTransfer,
  unitPrice,
  availablePlanTypes,
}) => {
  const { t } = useTranslate();
  const societyPlans = useSelector((state: State) => state.plans);

  const previewPlanType = useMemo(() => {
    const planTypes =
      availablePlanTypes ?? societyPlans.map((plan) => plan.planType);
    if (planTypes.includes('STOCK_OPTION')) return 'STOCK_OPTION';
    if (planTypes.includes('PHANTOM_SHARE')) return 'PHANTOM_SHARE';
    if (planTypes.includes('WARRANT')) return 'WARRANT';
    return 'STOCK_OPTION';
  }, [societyPlans, availablePlanTypes]);

  const previewPlanLabel = useMemo(() => {
    if (previewPlanType === 'PHANTOM_SHARE') return t('PhantomShares');
    if (previewPlanType === 'WARRANT') return t('Warrants');
    return t('StockOptions');
  }, [previewPlanType, t]);

  const previewMaxUnits = useMemo(
    () =>
      calculatePreviewMaxUnitsSellerCanOffer({
        sellerParticipationLimits,
        totalAvailableUnits: PREVIEW_AVAILABLE_UNITS,
        unitPrice,
        planType: previewPlanType,
        offerRemainingUnits: sharesToTransfer || undefined,
      }),
    [sellerParticipationLimits, sharesToTransfer, unitPrice, previewPlanType]
  );

  const needsMultipleSellers =
    sharesToTransfer > 0 && previewMaxUnits > 0 && sharesToTransfer > previewMaxUnits;

  return (
    <div className="alert alert-light mb-0 h-100">
      <h6 className="alert-heading fs-13px mb-2">
        {t('IncentivesMarketSellerLimitsPreview')}
      </h6>
      <p className="fs-13px mb-2">
        {t('IncentivesMarketSellerLimitsPreviewExample', {
          availableUnits: formatNumber(PREVIEW_AVAILABLE_UNITS),
          planType: previewPlanLabel,
          maxUnits: formatNumber(previewMaxUnits),
        })}
      </p>
      {needsMultipleSellers && (
        <span className="badge badge-dim badge-primary">
          {t('IncentivesMarketSellerLimitsMultipleSellersHint', {
            totalUnits: formatNumber(sharesToTransfer),
          })}
        </span>
      )}
    </div>
  );
};

export default BuyOfferSellerLimitsPreview;
